import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { ClipboardList, Copy, Edit, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import type { MessagingRule } from "@shared/schema";
import { RuleActivityDrawer } from "./RuleActivityDrawer";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useBetaAccess } from "@/hooks/use-beta-access";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  messageHasBookingRequestLinkToken,
  NEW_BOOKING_RULE_LINK_MISSING_WARNING,
} from "@shared/booking-request-link-token";
import { isComingSoonEventRule, COMING_SOON_RULE_ENABLE_TOAST } from "@/lib/message-rule-event-triggers";
import { PendingMessageRow } from "./_shared";
import { countFutureScheduledForRule, ruleSourceValue } from "@/lib/schedule-source-filter";



// ─── Rules Tab ────────────────────────────────────────────────────────────────
export type RulesFilter = "all" | "enabled" | "disabled";


export function RulesTab({
  canManage,
  navigate,
  toast,
}: {
  canManage: boolean;
  navigate: (path: string) => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const queryClient = useQueryClient();
  const rulesQuery = useQuery<MessagingRule[]>({ queryKey: ["/api/app/messaging-rules"] });
  // Incoming Message rules are beta-gated — without the grant, existing
  // incoming rules (e.g. Pulse-prefilled ones) can be viewed/edited/disabled
  // but not enabled, matching the gated radio on the rule form.
  const incomingBeta = useBetaAccess("incoming_message_rules");
  // Task #4721 — keyword rules beta gate (fail closed while loading).
  const keywordRulesGranted = useBetaAccess("keyword_rules");

  const [ruleToDelete, setRuleToDelete] = useState<MessagingRule | null>(null);
  const [activityRule, setActivityRule] = useState<MessagingRule | null>(null);
  // When set, a scheduled rule the host is trying to disable still has queued
  // auto-send messages; the confirmation dialog explains they'll still send and
  // links to the Schedule tab (filtered to this rule) to cancel them. `count`
  // is the number still queued, or `null` when the pending list couldn't be
  // loaded (fail-safe: we still warn rather than silently disable).
  const [ruleToDisable, setRuleToDisable] = useState<{ rule: MessagingRule; count: number | null } | null>(null);
  // When set, the host is trying to re-enable a New Booking rule whose message
  // is missing the {{booking_request_link:<id>}} token — mirror the rule form's
  // deliberate type-"proceed" confirmation before the enable PATCH fires.
  const [ruleToEnableWithoutLink, setRuleToEnableWithoutLink] = useState<MessagingRule | null>(null);
  const [enableWithoutLinkConfirmText, setEnableWithoutLinkConfirmText] = useState("");
  const [rulesFilter, setRulesFilter] = useLocalStorageState<RulesFilter>(
    "pp:rules-filter",
    "all",
    (stored) => (stored === "enabled" || stored === "disabled" ? stored : "all"),
  );
  const allRules = rulesQuery.data ?? [];
  // A "Coming soon" event rule is treated as disabled regardless of its stored
  // `enabled` flag, so counts/filters match what the row actually renders.
  const isEffectivelyEnabled = (r: MessagingRule) => !!r.enabled && !isComingSoonEventRule(r);
  const enabledCount = allRules.filter(isEffectivelyEnabled).length;
  const disabledCount = allRules.length - enabledCount;
  const visibleRules = useMemo(() => {
    if (rulesFilter === "enabled") return allRules.filter(isEffectivelyEnabled);
    if (rulesFilter === "disabled") return allRules.filter((r) => !isEffectivelyEnabled(r));
    return allRules;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRules, rulesFilter]);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/messaging-rules/${id}`),
    onSuccess: () => {
      toast({ title: "Rule deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messaging-rules"] });
    },
    onError: (err) => toast({ title: "Couldn't delete rule", description: parseErrorMessage(err, "Couldn't delete rule. Please try again."), variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: number; enabled: boolean }) =>
      apiRequest("PATCH", `/api/app/messaging-rules/${id}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/messaging-rules"] });
    },
    onError: (err) => toast({ title: "Couldn't update rule", description: parseErrorMessage(err, "Couldn't update rule. Please try again."), variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/messaging-rules/${id}/duplicate`);
      return (await res.json()) as MessagingRule;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/messaging-rules"] });
      toast({ title: "Rule duplicated", description: `Opening "${created.name}" for editing.` });
      navigate(`/app/messages/rules/${created.id}`);
    },
    onError: (err) =>
      toast({
        title: "Couldn't duplicate rule",
        description: parseErrorMessage(err, "Couldn't duplicate rule. Please try again."),
        variant: "destructive",
      }),
  });

  // Decide whether disabling a scheduled rule needs the warning dialog. We must
  // know whether the rule still has queued auto-send messages BEFORE deciding,
  // so we always force a CURRENT read of the pending list via `fetchQuery`
  // (staleTime: 0). Reading `pendingQuery.data` directly is unsafe: it can be
  // absent (first load) OR stale cached data that's missing newly materialized
  // scheduled messages while a background refetch is still in flight — either
  // would let the host silently disable without the warning. On a load error we
  // fail safe: warn (count unknown) rather than disable without telling them.
  async function requestDisableScheduledRule(rule: MessagingRule) {
    let pending: PendingMessageRow[];
    try {
      pending = await queryClient.fetchQuery<PendingMessageRow[]>({
        queryKey: ["/api/app/messages/pending"],
        staleTime: 0,
      });
    } catch {
      setRuleToDisable({ rule, count: null });
      return;
    }
    const count = countFutureScheduledForRule(pending ?? [], rule.id);
    if (count > 0) {
      setRuleToDisable({ rule, count });
      return;
    }
    toggleMutation.mutate({ id: rule.id, enabled: false });
  }

  const filterOptions: { value: RulesFilter; label: string; count: number }[] = [
    { value: "all", label: "All", count: allRules.length },
    { value: "enabled", label: "Enabled", count: enabledCount },
    { value: "disabled", label: "Disabled", count: disabledCount },
  ];

  return (
    <div className="h-full overflow-auto p-6" data-testid="tab-content-rules" data-tour="messages-rules">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Messaging Rules</h2>
        {canManage && (
          <Button
            size="sm"
            asChild
            data-testid="button-new-rule"
          >
            <Link href="/app/messages/rules/new"><Plus className="mr-1 h-4 w-4" /> New Rule</Link>
          </Button>
        )}
      </div>

      {!rulesQuery.isLoading && allRules.length > 0 && (
        <div
          className="mb-3 inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5"
          role="tablist"
          data-testid="filter-rules-status"
        >
          {filterOptions.map((opt) => {
            const active = rulesFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRulesFilter(opt.value)}
                className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`button-filter-rules-${opt.value}`}
              >
                <span>{opt.label}</span>
                <span
                  className={`rounded-full px-1.5 text-[10px] tabular-nums ${
                    active ? "bg-blue-200 text-blue-800" : "bg-gray-100 text-gray-500"
                  }`}
                  data-testid={`text-filter-rules-${opt.value}-count`}
                >
                  {opt.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {rulesQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : allRules.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-400">
          No messaging rules yet. Create one to automate outbound messages.
        </Card>
      ) : visibleRules.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-400" data-testid="empty-rules-filter">
          No {rulesFilter} rules to show. Adjust the filter above to see other rules.
        </Card>
      ) : (
        <div className="space-y-2">
          {visibleRules.map((rule) => {
            // "Coming soon" event rules are stuck disabled: render them off and
            // block enabling, even if an older `enabled: true` is still stored.
            const stuckDisabled = isComingSoonEventRule(rule);
            const effectiveEnabled = !!rule.enabled && !stuckDisabled;
            return (
            <Card key={rule.id} className="p-4" data-testid={`card-rule-${rule.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900" data-testid={`text-rule-name-${rule.id}`}>{rule.name}</span>
                    {rule.ruleType && (
                      <Badge variant="outline" className="text-[10px]">
                        {rule.ruleType === "business_hours" ? "business hours" : rule.ruleType}
                      </Badge>
                    )}
                  </div>
                  {rule.eventTrigger && (
                    <div className="mt-1 text-xs text-gray-500">
                      Trigger: <span className="font-medium">{rule.eventTrigger}</span>
                    </div>
                  )}
                  {rule.ruleType === "incoming_keywords" && rule.keywords && rule.keywords.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`keywords-summary-${rule.id}`}>
                      {rule.keywords.slice(0, 4).map((kw: string) => (
                        <span key={kw} className="inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-200">{kw}</span>
                      ))}
                      {rule.keywords.length > 4 && (
                        <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">+{rule.keywords.length - 4} more</span>
                      )}
                    </div>
                  )}
                  {rule.messageContent && (
                    <div className="mt-1 truncate text-xs text-gray-500">{rule.messageContent}</div>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
                    <span>Approval: {rule.messageApproval === "send_automatically" ? "Auto-send" : "Requires review"}</span>
                    {rule.sendTiming === "after_delay" && rule.delayMinutes != null && (
                      <span>Delay: {rule.delayMinutes % 1440 === 0 ? `${rule.delayMinutes / 1440}d` : rule.delayMinutes % 60 === 0 ? `${rule.delayMinutes / 60}h` : `${rule.delayMinutes}m`}</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canManage && (
                    <>
                      <div className="flex items-center gap-2 pr-1">
                        <Switch
                          checked={effectiveEnabled}
                          onCheckedChange={(checked) => {
                            // "Coming soon" event rules can't be enabled yet —
                            // block the turn-on and explain why (turning off is
                            // always allowed for legacy rows that slipped through).
                            if (checked && stuckDisabled) {
                              toast({ title: COMING_SOON_RULE_ENABLE_TOAST, variant: "destructive" });
                              return;
                            }
                            // Task #4721 — keyword/incoming rules are beta-gated.
                            // Either grant (keyword_rules or incoming_message_rules)
                            // unlocks the enable toggle; the server PATCH accepts
                            // either too. Disabling is always allowed.
                            if (checked && rule.ruleType === "incoming_keywords" && !keywordRulesGranted && !incomingBeta) {
                              toast({
                                title: "Keyword rules are in beta",
                                description: "Ask PadPulse support for access to enable keyword message rules.",
                                variant: "destructive",
                              });
                              return;
                            }
                            // Task #4705 — a "Booking Form Not Completed" rule
                            // can't be enabled without the booking-form link
                            // (the server PATCH rejects it too; this avoids a
                            // confusing raw 400).
                            if (
                              checked &&
                              rule.ruleType === "event" &&
                              rule.eventTrigger === "booking_form_followup" &&
                              !messageHasBookingRequestLinkToken(rule.messageContent)
                            ) {
                              toast({
                                title: "Booking request link required",
                                description: "Edit this rule and insert a booking request link before enabling it.",
                                variant: "destructive",
                              });
                              return;
                            }
                            // Disabling a scheduled rule does NOT cancel the
                            // messages it already queued for the next 7 days —
                            // warn first when any are still pending so the host
                            // can cancel the ones they don't want. The decision
                            // waits for the pending list (see helper) so it
                            // can't be bypassed by toggling before it loads.
                            if (!checked && rule.ruleType === "scheduled") {
                              void requestDisableScheduledRule(rule);
                              return;
                            }
                            // Re-enabling a New Booking rule whose message has
                            // no booking-form link token means submissions
                            // won't be tracked by PadPulse — require the same
                            // deliberate type-"proceed" confirmation as the
                            // rule form before firing the enable PATCH.
                            if (
                              checked &&
                              rule.ruleType === "event" &&
                              rule.eventTrigger === "new_booking" &&
                              !messageHasBookingRequestLinkToken(rule.messageContent)
                            ) {
                              setEnableWithoutLinkConfirmText("");
                              setRuleToEnableWithoutLink(rule);
                              return;
                            }
                            toggleMutation.mutate({ id: rule.id, enabled: checked });
                          }}
                          disabled={toggleMutation.isPending}
                          aria-label={effectiveEnabled ? "Disable rule" : "Enable rule"}
                          data-testid={`switch-toggle-rule-${rule.id}`}
                        />
                        <span
                          className={`text-xs font-medium ${
                            effectiveEnabled ? "text-green-700" : "text-gray-500"
                          }`}
                          data-testid={`text-rule-status-${rule.id}`}
                        >
                          {effectiveEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            data-testid={`button-rule-menu-${rule.id}`}
                            aria-label="Rule actions"
                            title="Rule actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            asChild
                            data-testid={`menu-edit-rule-${rule.id}`}
                          >
                            <Link href={`/app/messages/rules/${rule.id}`}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => duplicateMutation.mutate(rule.id)}
                            disabled={duplicateMutation.isPending}
                            data-testid={`menu-duplicate-rule-${rule.id}`}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setActivityRule(rule)}
                            data-testid={`menu-activity-rule-${rule.id}`}
                          >
                            <ClipboardList className="mr-2 h-4 w-4" />
                            Activity log
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-700"
                            onClick={() => setRuleToDelete(rule)}
                            disabled={deleteMutation.isPending}
                            data-testid={`menu-delete-rule-${rule.id}`}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}
      <AlertDialog open={!!ruleToDelete} onOpenChange={(v) => !v && setRuleToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-rule-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule?</AlertDialogTitle>
            <AlertDialogDescription>
              {ruleToDelete ? `Delete rule "${ruleToDelete.name}"? This cannot be undone.` : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-rule">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-rule"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!ruleToDelete) return;
                const id = ruleToDelete.id;
                deleteMutation.mutate(id, {
                  onSettled: () => setRuleToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disabling a scheduled rule leaves its already-queued messages intact —
          warn the host and offer a deep link to the Schedule tab (filtered to
          this rule) so they can cancel the ones they don't want. */}
      <AlertDialog open={!!ruleToDisable} onOpenChange={(v) => !v && setRuleToDisable(null)}>
        <AlertDialogContent data-testid="dialog-disable-rule-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              {ruleToDisable && ruleToDisable.count == null && (
                <>
                  Some messages from “{ruleToDisable.rule.name}” may already be scheduled to send over
                  the next 7 days. Disabling this rule stops it from scheduling new messages, but any
                  already-scheduled messages{" "}
                  <span className="font-semibold">will still be sent</span>. To stop any of them, go to
                  the Schedule tab and cancel them individually.
                </>
              )}
              {ruleToDisable && ruleToDisable.count != null && (
                <>
                  <span className="font-semibold">
                    {ruleToDisable.count} message{ruleToDisable.count === 1 ? "" : "s"}
                  </span>{" "}
                  from “{ruleToDisable.rule.name}” {ruleToDisable.count === 1 ? "is" : "are"} already
                  scheduled to send over the next 7 days. Disabling this rule stops it from scheduling
                  new messages, but these already-scheduled messages{" "}
                  <span className="font-semibold">will still be sent</span>. To stop any of them, go to
                  the Schedule tab and cancel them individually.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-disable-rule">Keep rule on</AlertDialogCancel>
            <Button
              variant="outline"
              data-testid="button-view-scheduled-from-disable"
              onClick={() => {
                if (!ruleToDisable) return;
                const ruleId = ruleToDisable.rule.id;
                setRuleToDisable(null);
                const params = new URLSearchParams({
                  tab: "scheduled",
                  source: ruleSourceValue(ruleId),
                });
                navigate(`/app/messages?${params.toString()}`);
              }}
            >
              View scheduled messages
            </Button>
            <AlertDialogAction
              data-testid="button-confirm-disable-rule"
              disabled={toggleMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!ruleToDisable) return;
                const id = ruleToDisable.rule.id;
                toggleMutation.mutate(
                  { id, enabled: false },
                  { onSettled: () => setRuleToDisable(null) },
                );
              }}
            >
              {toggleMutation.isPending ? "Disabling…" : "Disable rule"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-enabling a New Booking rule whose message lacks the booking-form
          link token — same deliberate type-"proceed" confirmation as the rule
          form's save dialog, before the enable PATCH fires. */}
      <Dialog
        open={!!ruleToEnableWithoutLink}
        onOpenChange={(open) => {
          if (!open && toggleMutation.isPending) return;
          if (!open) setRuleToEnableWithoutLink(null);
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            if (toggleMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (toggleMutation.isPending) e.preventDefault();
          }}
          data-testid="dialog-enable-missing-booking-link"
        >
          <DialogHeader>
            <DialogTitle>Enable without a booking request link?</DialogTitle>
            <DialogDescription data-testid="text-enable-missing-booking-link-warning">
              {NEW_BOOKING_RULE_LINK_MISSING_WARNING}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm space-y-1.5">
            <p className="font-medium text-blue-900">Didn't mean to do this?</p>
            <p className="text-blue-800">
              {ruleToEnableWithoutLink ? (
                <>
                  <Link
                    href={`/app/messages/rules/${ruleToEnableWithoutLink.id}`}
                    className="underline font-medium"
                    onClick={() => setRuleToEnableWithoutLink(null)}
                  >
                    Edit this rule
                  </Link>{" "}
                  and insert a dynamic placeholder for a booking request from the dropdown next to
                  the message content, or{" "}
                </>
              ) : (
                <>Edit the rule and insert a dynamic placeholder for a booking request, or{" "}</>
              )}
              <Link
                href="/app/members/booking-requests"
                className="underline font-medium"
                onClick={() => setRuleToEnableWithoutLink(null)}
              >
                go to Booking Requests
              </Link>{" "}
              to create one.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="enable-missing-link-confirm-input">
              Type <span className="font-semibold">proceed</span> to confirm
            </Label>
            <Input
              id="enable-missing-link-confirm-input"
              value={enableWithoutLinkConfirmText}
              onChange={(e) => setEnableWithoutLinkConfirmText(e.target.value)}
              placeholder="proceed"
              autoComplete="off"
              data-testid="input-enable-missing-booking-link-confirm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRuleToEnableWithoutLink(null)}
              disabled={toggleMutation.isPending}
              data-testid="button-enable-missing-booking-link-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!ruleToEnableWithoutLink) return;
                const id = ruleToEnableWithoutLink.id;
                toggleMutation.mutate(
                  { id, enabled: true },
                  { onSettled: () => setRuleToEnableWithoutLink(null) },
                );
              }}
              disabled={
                enableWithoutLinkConfirmText.trim().toLowerCase() !== "proceed" ||
                toggleMutation.isPending
              }
              data-testid="button-enable-missing-booking-link-accept"
            >
              {toggleMutation.isPending ? "Enabling..." : "Accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RuleActivityDrawer
        open={activityRule != null}
        onClose={() => setActivityRule(null)}
        rule={activityRule}
      />
    </div>
  );
}
